import { fetchMockVoicemailsBatch, getMockVoiceMessage, getMockVMTranscript } from './WebexMockVoicemailService';
import config from '../../../../config';

// ---------------------------------------------------------------------------
// Voicemail client cache
// ---------------------------------------------------------------------------
// The Webex `VoicemailClient` (from `@webex/calling`) captures the current
// authorization headers from the `window.webex` SDK instance during its
// `init()` call and reuses them for every subsequent voicemail request.
// Previously we created and initialized a brand new client on every batch
// fetch (and again on every getMessage / delete / read / transcript call),
// which:
//   * caused dozens of redundant `init()` round trips
//   * still left every individual client with whatever auth headers were
//     present when *it* was initialized (so a long-lived stale client kept
//     issuing 401s even after the SDK token had been refreshed)
// We now cache a single client per Webex device URL and expose
// `invalidateVoicemailClient()` so the LandingPage 401 handler can drop the
// stale client whenever the SDK is re-initialized or the token is refreshed.
// ---------------------------------------------------------------------------
let cachedVoicemailClient: any | null = null;
let cachedVoicemailClientDeviceUrl: string | null = null;
let cachedVoicemailClientInitPromise: Promise<any> | null = null;
let cachedVoicemailClientInitToken: object | null = null;
let cachedVoicemailClientGeneration = 0;

/**
 * Drops the cached voicemail client. Call this whenever the Webex SDK is
 * re-initialized, the access token is refreshed, or a 401 is observed so
 * the next voicemail request rebuilds a client against the fresh SDK
 * credentials.
 */
export function invalidateVoicemailClient(): void {
  if (cachedVoicemailClient) {
    Logger.info('WebexVoicemailService.invalidateVoicemailClient(): dropping cached client.');
  }
  cachedVoicemailClientGeneration += 1;
  cachedVoicemailClient = null;
  cachedVoicemailClientDeviceUrl = null;
  cachedVoicemailClientInitPromise = null;
  cachedVoicemailClientInitToken = null;
}

function getCurrentDeviceUrl(): string | null {
  try {
    const webex: any = (window as any)?.webex;
    const url = webex?.internal?.device?.url
      ?? (typeof webex?.internal?.device?.getId === 'function' ? webex.internal.device.getId() : null);
    return typeof url === 'string' && url.length > 0 ? url : null;
  } catch {
    return null;
  }
}

async function getOrCreateVoicemailClient(): Promise<any> {
  const currentDeviceUrl = getCurrentDeviceUrl();
  // Reuse cached client only when SDK identity is unchanged.
  if (
    cachedVoicemailClient
    && currentDeviceUrl
    && cachedVoicemailClientDeviceUrl === currentDeviceUrl
  ) {
    return cachedVoicemailClient;
  }

  // If a different concurrent caller is already initializing, await it.
  if (cachedVoicemailClientInitPromise) {
    return cachedVoicemailClientInitPromise;
  }

  // Rebuild the client; clear any partial state first.
  cachedVoicemailClient = null;
  cachedVoicemailClientDeviceUrl = null;

  const initGeneration = cachedVoicemailClientGeneration;
  const initToken = {};
  cachedVoicemailClientInitToken = initToken;
  const isCurrentInitialization = () =>
    cachedVoicemailClientGeneration === initGeneration
    && cachedVoicemailClientInitToken === initToken;
  const initPromise = (async () => {
    try {
      Logger.info('WebexVoicemailService.getOrCreateVoicemailClient(): creating new voicemail client.');
      const client = createVoicemailClient((window as any)?.webex, { level: LOGGER.INFO });
      await client?.init();
      if (isCurrentInitialization()) {
        cachedVoicemailClient = client;
        cachedVoicemailClientDeviceUrl = getCurrentDeviceUrl();
      }
      return client;
    } catch (error) {
      if (isCurrentInitialization()) {
        cachedVoicemailClient = null;
        cachedVoicemailClientDeviceUrl = null;
      }
      Logger.error(
        `WebexVoicemailService.getOrCreateVoicemailClient(): init failed: '${formatError(error)}'.`
      );
      throw error;
    } finally {
      if (isCurrentInitialization()) {
        cachedVoicemailClientInitPromise = null;
        cachedVoicemailClientInitToken = null;
      }
    }
  })();

  cachedVoicemailClientInitPromise = initPromise;
  return initPromise;
}

// Fetch a single batch of voicemails for infinite scroll (on-demand)
export async function fetchVoicemailsBatch(offset: number, limit: number): Promise<IVoiceMailResponse> {
  // Use mock only for Webex Calling in developer mode
  const callingBackend = getCallingBackEnd();
  const isDev = config.APP_ENVIRONMENT === 'Dev_Environment';
  if (callingBackend === CALLING_BACKEND.WXC && isDev) {
    return fetchMockVoicemailsBatch(offset, limit);
  }
  const callvoicemailClient = await getOrCreateVoicemailClient();
  const response = await callvoicemailClient.getVoicemailList(
    offset,
    limit,
    SORT.DESC,
    true
  );
  const batch = response as unknown as IVoiceMailResponse;
  return batch;
}
// import { createVoicemailClient } from "@webex/web-calling-sdk";
import { createVoicemailClient } from '@webex/calling';
import { DBSchema, IDBPDatabase, openDB } from "idb";
import { formatError, Logger, LOGGER } from "../../../../packages/logger/src";
import {
  IDB_STORAGE,
  IDB_VM_TRANSCRIPT_STORAGE,
  IVoiceMailDeleteResponse,
  IVoiceMailList,
  IVoiceMailReadResponse,
  IVoiceMailResponse,
  IVoiceMailTranscriptMessage,
  IVoiceMessageContentResponse,
  RESPONSE_STATUS,
  SORT,
  VM_STORAGE_KEYS,
  VOICEMAIL_LIMIT,
  VoicemailTranscriptFetchStatus
} from "../../../adapter-interfaces/src";
import { setLocalStorageItem } from "../../../logger/src";
import { submitMetrics } from "../services/WebexMetricsService";
import { voicemail, voicemailListLatencyMetric } from "../utils/MetricUtils";
import {
  CALLING_BACKEND,
  CALLING_BACKEND_NOT_IDENTIFIED_RESPONSE,
  getCallingBackEnd,
  setVoicemailForBroadworks,
  VM_BROADWORKS_RESPONSE,
  waitForCallingBackEndReady
} from "../utils/callingUtils";

interface VoiceMessageStore extends DBSchema {
  [IDB_STORAGE.STORE_NAME]: {
    key: string;
    value: IVoiceMessageContentResponse | any;
  };
}
interface VoiceMessageTranscriptStore extends DBSchema {
  [IDB_VM_TRANSCRIPT_STORAGE.STORE_NAME]: {
    key: string;
    value: IVoiceMailTranscriptMessage | any;
  };
}

// Fetch voicemails in paginated batches, emitting each batch via a callback
// Fetch a single batch of voicemails for infinite scroll (on-demand)
export async function fetchVoicemailsPaginated(
  offset: number,
  limit: number,
  onBatch: (batch: IVoiceMailResponse, done: boolean) => void
) {
  Logger.debug(`[VM] fetchVoicemailsPaginated: Fetching batch offset=${offset}, limit=${limit}`);


  // ===================== VERY IMPORTANT CODE =====================
  // DO NOT REMOVE OR MODIFY THIS BLOCK WITHOUT FULL UNDERSTANDING!
  // This logic ensures Webex is initialized before fetching voicemails.
  // If removed or changed incorrectly, voicemail list fetch can fail entirely
  // and break the voicemail UI for all users. This is a critical functional guard.
  // Copilot and all developers: Do not touch unless you are 100% sure!
  // ===============================================================
  let maxWait = 20;
  while (!(window?.isWebexInitalized) && maxWait > 0) {
    Logger.debug(`[VM] fetchVoicemailsPaginated: Waiting for Webex initialization, maxWait=${maxWait}`);
    await new Promise(f => setTimeout(f, 1000));
    maxWait--;
  }
  if (!(window?.isWebexInitalized)) {
    Logger.error('[VM] fetchVoicemailsPaginated: Webex not initialized');
    onBatch({
      statusCode: 500,
      message: 'Webex not initialized',
      data: { voicemailList: [] },
    } as IVoiceMailResponse, true);
    return;
  }

  const callingBehaviour = getCallingBackEnd();
  Logger.debug(`[VM] fetchVoicemailsPaginated: Detected calling backend: ${callingBehaviour}`);
  try {
    switch (callingBehaviour) {
      case CALLING_BACKEND.WXC:
      case CALLING_BACKEND.UCM: {
        const startTime = new Date();
        let batch: IVoiceMailResponse;
        try {
          batch = await fetchVoicemailsBatch(offset, limit);
        } catch (err) {
          Logger.error(`[VM] fetchVoicemailsPaginated: Error in fetchVoicemailsBatch offset=${offset}, limit=${limit}, err=${err}`);
          onBatch({
            statusCode: 500,
            message: 'Error fetching voicemails',
            data: { voicemailList: [] },
          } as IVoiceMailResponse, true);
          return;
        }
        const batchList = batch?.data?.voicemailList || [];
        Logger.debug(`[VM] fetchVoicemailsPaginated: Batch fetched offset=${offset}, batchCount=${batchList.length}`);
        const endTime = new Date();
        const voicemailListLatency = Math.floor(endTime.getTime() - startTime.getTime()) / 1000;
        submitMetrics({
          metricEvent: voicemailListLatencyMetric,
          client_timestamp: voicemail,
          trackingId: voicemail,
          key: voicemail,
          voicemailListLatency: `${voicemailListLatency}`,
        });
        const done = batchList.length < limit;
        onBatch(batch, done);
        break;
      }
      case CALLING_BACKEND.BWRKS: {
        const isBroadworksFTEnabled = await setVoicemailForBroadworks();
        Logger.info(`[VM] fetchVoicemailsPaginated: Is Broadworks feature toggle enabled? : ${isBroadworksFTEnabled}`);
        if (isBroadworksFTEnabled) {
          const startTime = new Date();
          let batch: IVoiceMailResponse;
          try {
            batch = await fetchVoicemailsBatch(offset, limit);
          } catch (err) {
            Logger.error(`[VM] fetchVoicemailsPaginated: Error in fetchVoicemailsBatch (BWRKS) offset=${offset}, limit=${limit}, err=${err}`);
            onBatch({
              statusCode: 500,
              message: 'Error fetching voicemails',
              data: { voicemailList: [] },
            } as IVoiceMailResponse, true);
            return;
          }
          const batchList = batch?.data?.voicemailList || [];
          Logger.debug(`[VM] fetchVoicemailsPaginated: Batch fetched (BWRKS) offset=${offset}, batchCount=${batchList.length}`);
          const endTime = new Date();
          const voicemailListLatency = Math.floor(endTime.getTime() - startTime.getTime()) / 1000;
          submitMetrics({
            metricEvent: voicemailListLatencyMetric,
            client_timestamp: voicemail,
            trackingId: voicemail,
            key: voicemail,
            voicemailListLatency: `${voicemailListLatency}`,
          });
          const done = batchList.length < limit;
          onBatch(batch, done);
        } else {
          Logger.info('[VM] fetchVoicemailsPaginated: Broadworks feature toggle not enabled, hiding VM tab');
          setLocalStorageItem(VM_STORAGE_KEYS.IS_HIDE_VM_TAB, true);
          window.dispatchEvent(new Event('storage'));
          onBatch(VM_BROADWORKS_RESPONSE as IVoiceMailResponse, true);
        }
        break;
      }
      default: {
        Logger.info('[VM] fetchVoicemailsPaginated: Unknown calling backend, hiding VM tab');
        setLocalStorageItem(VM_STORAGE_KEYS.IS_HIDE_VM_TAB, true);
        window.dispatchEvent(new Event('storage'));
        onBatch(CALLING_BACKEND_NOT_IDENTIFIED_RESPONSE as IVoiceMailResponse, true);
        break;
      }
    }
  } catch (err) {
    Logger.error(`[VM] fetchVoicemailsPaginated: Unexpected error: ${err}`);
    onBatch({
      statusCode: 500,
      message: 'Unexpected error in fetchVoicemailsPaginated',
      data: { voicemailList: [] },
    } as IVoiceMailResponse, true);
  }
}

export async function getVoiceMailList(): Promise<IVoiceMailResponse> {
  // Always use paginated fetch for all cases
  let allVoicemails: IVoiceMailList[] = [];
  let offset = VOICEMAIL_LIMIT.OFFSET;
  let done = false;
  while (!done) {
    await fetchVoicemailsPaginated(offset, VOICEMAIL_LIMIT.BATCH_SIZE, (batch, isDone) => {
      if (batch?.data?.voicemailList) {
        allVoicemails = allVoicemails.concat(batch.data.voicemailList);
      }
      done = isDone;
    });
    offset += VOICEMAIL_LIMIT.BATCH_SIZE;
  }
  return {
    statusCode: 200,
    message: 'Fetched all voicemails',
    data: { voicemailList: allVoicemails },
  } as IVoiceMailResponse;
}

export async function deleteVoiceMail(
  voicemailId: string
): Promise<IVoiceMailDeleteResponse> {
  try {
    const callvoicemailClient = await getOrCreateVoicemailClient();
    const response = await callvoicemailClient.deleteVoicemail(voicemailId);
    Logger.info(`WebexVoicemailService:deleteVoiceMail(): done.`);
    return response as IVoiceMailDeleteResponse;
  } catch (error) {
    Logger.error(
      `WebexVoicemailService.deleteVoiceMail(): got error: '${formatError(
        error
      )}'.`
    );
    throw error;
  }
}

async function getDb(): Promise<IDBPDatabase<VoiceMessageStore>> {
  const db = await openDB<VoiceMessageStore>(
    IDB_STORAGE.DB_NAME,
    IDB_STORAGE.DB_VERSION,
    {
      upgrade(db) {
        db.createObjectStore(IDB_STORAGE.STORE_NAME, { keyPath: "key" });
      },
    }
  );
  return db;
}

async function getVoiceMessageFromApi(
  voicemailId: string
): Promise<IVoiceMessageContentResponse> {
  // Use mock only for Webex Calling in developer mode
  const callingBackend = getCallingBackEnd();
  const isDev = config.APP_ENVIRONMENT === 'Dev_Environment';
  if (callingBackend === CALLING_BACKEND.WXC && isDev) {
    return getMockVoiceMessage(voicemailId);
  }
  try {
    const callvoicemailClient = await getOrCreateVoicemailClient();
    const result = await callvoicemailClient.getVoicemailContent(voicemailId);
    return {
      ...result,
      voiceMessageId: voicemailId,
      createdAt: new Date().toUTCString(),
    } as IVoiceMessageContentResponse;
  } catch (error) {
    Logger.error(
      `WebexVoicemailService.getFromAPI(): got error: '${formatError(error)}'.`
    );
    throw error;
  }
}

export async function getVoiceMessage(
  voicemailId: string
): Promise<IVoiceMessageContentResponse> {

  if (!(IDB_STORAGE.IDB_EXIST in window)) {
    return await getVoiceMessageFromApi(voicemailId);
  }

  try {
    let db = await getDb();
    let voiceMessage = await db.get(IDB_STORAGE.STORE_NAME, voicemailId);

    if (voiceMessage) {
      return voiceMessage.value;
    }

    const response = await getVoiceMessageFromApi(voicemailId);

    if (response.message === RESPONSE_STATUS.SUCCESS) {
      db = await openDB<VoiceMessageStore>(
        IDB_STORAGE.DB_NAME,
        IDB_STORAGE.DB_VERSION
      );
      const voiceMessageStore = await db
        .transaction(IDB_STORAGE.STORE_NAME, IDB_STORAGE.READ_WRITE)
        .objectStore(IDB_STORAGE.STORE_NAME);

      const currentLength = await voiceMessageStore.count();

      if (currentLength === IDB_STORAGE.MAX_ITEMS) {
        const getAllVMs = await voiceMessageStore.getAll();
        const firstRecord = getAllVMs.reduce((prev, curr) =>
          prev.value.createdAt > curr.value.createdAt ? curr : prev
        );
        await voiceMessageStore.delete(firstRecord.key);
      }
      await voiceMessageStore.put({ key: voicemailId, value: response });
    }
    return response as IVoiceMessageContentResponse;
  } catch (error) {
    Logger.error(
      `WebexVoicemailService.getVoiceMessage(): got error: '${formatError(
        error
      )}'.`
    );
    throw error;
  }
}

export async function readVoiceMessage(
  voicemailId: string
): Promise<IVoiceMailReadResponse> {
  try {
    const callvoicemailClient = await getOrCreateVoicemailClient();
    const response = await callvoicemailClient.voicemailMarkAsRead(voicemailId);
    Logger.info(`WebexVoicemailService:readVoiceMessage(): done.`);
    return response as IVoiceMailReadResponse;
  } catch (error) {
    Logger.error(
      `WebexVoicemailService.readVoiceMessage(): got error: '${formatError(
        error
      )}'.`
    );
    throw error;
  }
}

export async function unreadVoiceMessage(
  voicemailId: string
): Promise<IVoiceMailReadResponse> {
  try {
    const callvoicemailClient = await getOrCreateVoicemailClient();
    const response = await callvoicemailClient.voicemailMarkAsUnread(voicemailId);
    Logger.info(`WebexVoicemailService:unreadVoiceMessage(): done.`);
    return response as IVoiceMailReadResponse;
  } catch (error) {
    Logger.error(
      `WebexVoicemailService.unreadVoiceMessage(): got error: '${formatError(
        error
      )}'.`
    );
    throw error;
  }
}

async function getVMTranscriptDb(): Promise<IDBPDatabase<VoiceMessageTranscriptStore>> {
  const db = await openDB<VoiceMessageTranscriptStore>(
    IDB_VM_TRANSCRIPT_STORAGE.DB_NAME,
    IDB_VM_TRANSCRIPT_STORAGE.DB_VERSION,
    {
      upgrade(db) {
        db.createObjectStore(IDB_VM_TRANSCRIPT_STORAGE.STORE_NAME, { keyPath: "key" });
      },
    }
  );
  return db;
}

export async function getVMTranscript(voicemailId: string): Promise<IVoiceMailTranscriptMessage> {
  // Use mock only for Webex Calling in developer mode
  const callingBackend = getCallingBackEnd();
  const isDev = config.APP_ENVIRONMENT === 'Dev_Environment';
  if (callingBackend === CALLING_BACKEND.WXC && isDev) {
    return getMockVMTranscript(voicemailId);
  }

  try {
    const callvoicemailClient = await getOrCreateVoicemailClient();
    const response = await callvoicemailClient.getVMTranscript(voicemailId);
    Logger.info(`WebexVoicemailService:getVMTranscript(): done.`);
    return {
      ...response,
      voiceMessageId: voicemailId,
      createdAt: new Date().toUTCString(),
    } as IVoiceMailTranscriptMessage;
  } catch (error) {
    Logger.error(
      `WebexVoicemailService.getVMTranscriptFromAPI(): got error: '${formatError(
        error
      )}'.`
    );
    throw error;
  }
}

// export async function getVMTranscript(
//   voicemailId: string
// ): Promise<IVoiceMailTranscriptMessage> {
//   if (!(IDB_VM_TRANSCRIPT_STORAGE.IDB_EXIST in window)) {
//     return await getVMTranscriptFromAPI(voicemailId);
//   }
//   try {
//     let db = await getVMTranscriptDb();
//     let voiceMessageTranscript = await db.get(IDB_VM_TRANSCRIPT_STORAGE.STORE_NAME, voicemailId);

//     if (voiceMessageTranscript) {
//       return voiceMessageTranscript.value;
//     }

//     const response = await getVMTranscriptFromAPI(voicemailId);
//     if (response.message === VoicemailTranscriptFetchStatus.READY) {
//       db = await openDB<VoiceMessageTranscriptStore>(
//         IDB_VM_TRANSCRIPT_STORAGE.DB_NAME,
//         IDB_VM_TRANSCRIPT_STORAGE.DB_VERSION
//       );
//       const voiceMessageTranscriptStore = await db
//         .transaction(IDB_VM_TRANSCRIPT_STORAGE.STORE_NAME, IDB_VM_TRANSCRIPT_STORAGE.READ_WRITE)
//         .objectStore(IDB_VM_TRANSCRIPT_STORAGE.STORE_NAME);

//       const currentLength = await voiceMessageTranscriptStore.count();
//       if (currentLength === IDB_VM_TRANSCRIPT_STORAGE.MAX_ITEMS) {
//         const getAllVMs = await voiceMessageTranscriptStore.getAll();
//         const firstRecord = getAllVMs.reduce((prev, curr) =>
//           prev.value.createdAt > curr.value.createdAt ? curr : prev
//         );
//         await voiceMessageTranscriptStore.delete(firstRecord.key);
//       }
//       await voiceMessageTranscriptStore.put({ key: voicemailId, value: response });
//     }
//     return response as IVoiceMailTranscriptMessage;
//   } catch (error) {
//     Logger.error(
//       `WebexVoicemailService.getVMTranscript(): got error: '${formatError(
//         error
//       )}'.`
//     );
//     throw error;
//   }
// }

/**
 * Initializes the Webex calling backend control.
 * This function checks if the Webex calling backend feature is enabled for the user.
 * It checks if the user has the required license.
 *
 * @returns {Promise<boolean>} - A promise that resolves to a boolean indicating if the CX-Essentia feature is enabled.
 */
async function initWebexCallingBackend() {
  let isWebexCallingBackend = false;
  const IS_WEBEX_CALLING_BACKEND_USER ='isWebexCallingBackendUser';
  try {
    if(window?.isWebexInitalized) {
      const callingBehaviour = await waitForCallingBackEndReady();
      isWebexCallingBackend = callingBehaviour === 'WEBEX_CALLING';
      setLocalStorageItem(
        IS_WEBEX_CALLING_BACKEND_USER,
        isWebexCallingBackend
      );
    }
    Logger.info(`Is Webex calling backend?: ${isWebexCallingBackend}`);  
  } catch (error) {
    Logger.error(
      `Is Webex calling backend: got error: '${formatError(
        error
      )}'.`
    );
  }
  return isWebexCallingBackend;
}

/**
 * Retrieves the Webex calling backend control.
 * This function ensures that Webex calling backend control.
 * If Webex is not initialized, it waits for a second and retries.
 *
 * @returns {Promise<boolean>} - A promise that resolves to a boolean indicating if the  Webex calling backend control feature is enabled.
 */
export async function getWebexCallingBackendUser(): Promise<boolean> {
  if (window?.isWebexInitalized) {
    return initWebexCallingBackend();
  } else {
    await new Promise((f) => setTimeout(f, 1000));
    return getWebexCallingBackendUser();
  }
}
